import { HttpClient, HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from "@microsoft/signalr"
import { MessagePackHubProtocol } from "@microsoft/signalr-protocol-msgpack"

import { CommMessageModel } from "./comm-message-model"
import { environment } from 'src/environments/environment'

export class SignalrCommunicationEngine {
    private readonly msgHandlers: Array<[string, (msg: CommMessageModel) => void]> = []

    private errorHandler: (err: any) => void
    private connPromise: Promise<void> | null
    private connection: HubConnection | null

    constructor(
        private readonly signalrHubUrl: string,
        private readonly receivingRpcName: string,
        private readonly accessTokenFactory: () => Promise<string>,
        private readonly httpClient: HttpClient,
    ) {
        this.createConnection()
    }

    public onMsg(msgMethod: string, msgCallback: (msg: CommMessageModel) => void) {
        this.msgHandlers.push([msgMethod, msgCallback])
        this.connection?.on(msgMethod, (model: CommMessageModel) => msgCallback(model))
        return this
    }

    public offMsg(msgMethod: string, msgCallback: (msg: CommMessageModel) => void) {
        this.connection?.off(msgMethod, (model: CommMessageModel) => msgCallback(model))
        this.msgHandlers.splice(this.msgHandlers.findIndex(([meth,]) => meth === msgMethod), 1)
        return this
    }

    public onErr(errCallback: (err: any) => void) {
        this.errorHandler = errCallback
        this.connPromise?.catch(this.errorHandler)
        return this
    }

    public sendMsg(msg: CommMessageModel): void {
        const sendMsgInternal = (() => {
            msg.SenderId = this.connection?.connectionId || ''
            this.connection?.send(this.receivingRpcName, msg, '')
        }).bind(this)

        if (!this.connection || this.connection.state === HubConnectionState.Disconnected) {
            this.connect()?.then(() => sendMsgInternal())
        } else {
            sendMsgInternal()
        }
    }

    public close(): void {
        this.connection
            ?.stop()
            .then(() => {
                console.log('Connection stopped successfully.')
                this.connection = null!
            })
            .catch(err => {
                console.error('Error while trying to stop connection', err)
                this.connection = null!
            })
    }

    public async connect(): Promise<void | null> {
        if (!this.connection) {
            await this.createConnection()
        }

        if (this.connection?.state === HubConnectionState.Disconnected) {
            this.connPromise = this.connection.start()
                .catch(err => {
                    if (!this.errorHandler) {
                        console.log('Error in signalr service:', err, 'Connection state is:', this.connection)
                    } else {
                        this.errorHandler(err)
                    }
                })
        }

        return this.connPromise
    }

    private async createConnection(): Promise<void> {
        if (await this.accessTokenFactory() !== '') {
            this.connection = new HubConnectionBuilder()
                .withUrl(this.signalrHubUrl, { httpClient: this.httpClient, accessTokenFactory: this.accessTokenFactory })
                .withAutomaticReconnect()
                .withHubProtocol(new MessagePackHubProtocol())
                .configureLogging(environment.production ? LogLevel.None : LogLevel.Trace)
                .build()

            this.msgHandlers.forEach(([meth, clbk]) => this.connection?.on(meth, clbk))
        }
    }
}
